import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Gauge, Loader2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const VERDICT_STYLE = {
  TRACKING: 'bg-emerald-500/20 text-emerald-300', 'NEAR MAP': 'bg-emerald-500/20 text-emerald-300', 'STEP APPLIED': 'bg-emerald-500/20 text-emerald-300',
  LAGGING: 'bg-amber-500/20 text-amber-300', 'OFF MAP': 'bg-amber-500/20 text-amber-300', 'MAP-RELATIVE': 'bg-cyan-500/20 text-cyan-300',
  FAULT: 'bg-red-500/20 text-red-300', 'FAR FROM MAP': 'bg-red-500/20 text-red-300',
};
const badgeCls = (v) => VERDICT_STYLE[v] || (String(v).startsWith('OVERSHOOT') ? 'bg-red-500/20 text-red-300' : 'bg-zinc-700/40 text-zinc-300');

function authHeaders() {
  try { const t = localStorage.getItem('auth_token'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; }
}

function CamCell({ s, delta, unmatched }) {
  if (!s) return <td className="px-2 py-1.5 text-zinc-600">—</td>;
  return (
    <td className="px-2 py-1.5 whitespace-nowrap">
      <span className="text-white font-['JetBrains_Mono']">{s.actual_avg}°</span>
      {s.target_avg != null && <span className="text-zinc-500 text-[10px]"> / {s.target_avg}° tgt</span>}
      {delta != null && <span className={`ml-1 text-[10px] font-bold ${delta > 0 ? 'text-cyan-300' : 'text-zinc-400'}`}>{delta > 0 ? '+' : ''}{delta}°</span>}
      {delta == null && unmatched && <span className="ml-1 text-[9px] text-zinc-600">no WOT match</span>}
      {s.verdict && s.verdict !== 'NO TARGET' && <Badge className={`ml-1 rounded-sm text-[9px] px-1 py-0 ${badgeCls(s.verdict)}`}>{s.verdict}</Badge>}
    </td>
  );
}

export default function VanosScorecard({ datalog, datalogs = [] }) {
  const [data, setData] = useState(null);
  const [baselineId, setBaselineId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!datalog?.id) return;
    let cancelled = false;
    setLoading(true); setError(null);
    const q = baselineId ? `?baseline_id=${encodeURIComponent(baselineId)}` : '';
    fetch(`${API_URL}/api/datalogs/${datalog.id}/vanos${q}`, { headers: authHeaders() })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`); return j; })
      .then((j) => { if (!cancelled) setData(j); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [datalog?.id, baselineId]);

  const v = data?.vanos;
  const cmp = data?.comparison;
  const deltaFor = (band, cam) => cmp?.bands?.find((b) => b.band === band)?.[cam];
  const others = datalogs.filter((d) => d.id !== datalog?.id);

  return (
    <Card className="bg-[#111111] border border-[#27272A] rounded-sm" data-testid="vanos-scorecard">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Gauge className="w-5 h-5 text-[#FF5E1A]" />VANOS Cam Tracking
            {v?.available && <Badge className={`rounded-sm text-[10px] ${badgeCls(v.overall_verdict)}`} data-testid="vanos-overall-verdict">{v.overall_verdict}</Badge>}
          </CardTitle>
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] flex items-center gap-2">
            Baseline
            <select value={baselineId} onChange={(e) => setBaselineId(e.target.value)}
              className="bg-[#050505] border border-[#27272A] rounded-sm px-2 py-1 text-xs text-zinc-200 normal-case tracking-normal font-sans"
              data-testid="vanos-baseline-select">
              <option value="">— none —</option>
              {others.map((d) => <option key={d.id} value={d.id}>{d.filename}{d.tune_version ? ` (${d.tune_version})` : ''}</option>)}
            </select>
          </label>
        </div>
        <CardDescription className="text-zinc-400">
          Logged cam spread (WOT) per RPM band{v?.target_source ? ` · targets: ${v.target_source}` : ''}. Pick a baseline log to see the V29 step delta.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="vanos-loading"><Loader2 className="w-4 h-4 animate-spin" />Scoring cams…</div>}
        {error && <div className="text-xs text-red-400" data-testid="vanos-error">{error}</div>}
        {v && !v.available && <div className="text-xs text-zinc-400" data-testid="vanos-unavailable">{v.reason}</div>}
        {v?.available && (
          <div className="space-y-3">
            {cmp && (
              <div className="p-3 bg-gradient-to-br from-[#FF5E1A]/10 to-amber-500/5 border border-[#FF5E1A]/40 rounded-sm" data-testid="vanos-v29-delta">
                <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-200">
                  <span className="font-bold font-['Outfit'] text-[#FF5E1A]">4400-6500 WOT step vs baseline:</span>
                  <span>intake {cmp.v29_band_delta.intake == null ? '—' : `${cmp.v29_band_delta.intake > 0 ? '+' : ''}${cmp.v29_band_delta.intake}°`}</span>
                  <span>exhaust {cmp.v29_band_delta.exhaust == null ? '—' : `${cmp.v29_band_delta.exhaust > 0 ? '+' : ''}${cmp.v29_band_delta.exhaust}°`}</span>
                  <span className="text-zinc-500">(expected +{cmp.expected_step_degkw}° · matched WOT bands: {cmp.matched_bands?.length ? cmp.matched_bands.join(', ') : 'none'})</span>
                  <Badge className={`rounded-sm text-[10px] ${badgeCls(cmp.verdict)}`} data-testid="vanos-step-verdict">{cmp.verdict}</Badge>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="vanos-band-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] border-b border-[#27272A]">
                    <th className="text-left px-2 py-1.5">RPM band</th><th className="text-left px-2 py-1.5">WOT n</th>
                    <th className="text-left px-2 py-1.5">Intake spread</th><th className="text-left px-2 py-1.5">Exhaust spread</th>
                  </tr>
                </thead>
                <tbody>
                  {v.bands.map((b) => (
                    <tr key={b.band} className={`border-b border-[#1a1a1a] ${b.wot_samples ? '' : 'opacity-50'}`} data-testid={`vanos-band-${b.band}`}>
                      <td className="px-2 py-1.5 font-['JetBrains_Mono'] text-zinc-300">{b.band}</td>
                      <td className="px-2 py-1.5 text-zinc-400">{b.wot_samples}/{b.samples}</td>
                      <CamCell s={b.intake} delta={deltaFor(b.band, 'intake')} unmatched={!!cmp} />
                      <CamCell s={b.exhaust} delta={deltaFor(b.band, 'exhaust')} unmatched={!!cmp} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {v.valvetronic && (
              <p className="text-[11px] text-zinc-400" data-testid="vanos-valvetronic">
                Valvetronic eccentric shaft: avg error {v.valvetronic.error_abs_avg}°, max {v.valvetronic.error_abs_max}° · <span className={badgeCls(v.valvetronic.verdict).split(' ')[1]}>{v.valvetronic.verdict}</span>
              </p>
            )}
            {v.notes.map((n) => (
              <div key={n} className="flex gap-2 text-[11px] text-zinc-400"><Info className="w-3.5 h-3.5 shrink-0 text-cyan-400 mt-0.5" />{n}</div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
